import { type AuthStore, initialAuthStore } from "@storytell/auth";
import type { Named } from "@storytell/logger";
import { isError } from "@storytell/result";
import type { Auth } from "firebase/auth";
import { syncAuthWithAPI } from "./syncAuthWithAPI";

export const clientAuthResourceServer = async ({
  logger,
  deviceId,
  locale,
  userAgent,
  getFirebaseClientAuth,
}: {
  logger: Named;
  deviceId: string;
  locale: string;
  userAgent: string;
  getFirebaseClientAuth: () => Auth;
}): Promise<AuthStore> => {
  const auth = getFirebaseClientAuth();
  const user = auth.currentUser;
  if (!user) {
    return initialAuthStore();
  }

  const token = await user.getIdToken();

  const syncResponse = await syncAuthWithAPI({
    logger,
    firebaseUser: user,
    deviceId,
    locale,
    userAgent,
    token,
  });

  if (isError(syncResponse)) {
    logger.error("AUTH_ERROR", "Error syncing auth with API", {
      error: syncResponse.message,
      code: syncResponse.code,
    });
    return initialAuthStore();
  }
  // The sync hands back the token the control plane wants used from here on: when it
  // re-pins the Firebase custom claims it answers tokenRefreshReason, and the refreshed
  // token is the only one carrying the pinned realm. Keeping the pre-sync token would
  // leave every later request on claims the server has just replaced.
  const { authenticatedUser, token: syncedToken } = syncResponse.data;
  return {
    state: authenticatedUser.user.lifecycleStatus,
    authenticatedUser,
    token: syncedToken,
  };
};
